import type { CmsStoryContent, CmsCopy } from "../adapters";
import { type ApiConfiguration } from "./shared";

const getTranslatedSlug = (slug: string, config: ApiConfiguration) => {
  if (config.locale === "en-CH") return slug;

  const arr = slug.split("/");
  arr.shift();
  return arr.join("/");
};

const flattenObject = (obj: unknown, prefix: string, result: Record<string, string>) => {
  if (typeof obj === "string") {
    result[prefix] = obj;
  } else if (Array.isArray(obj)) {
    obj.forEach((item, index) => {
      flattenObject(item, `${prefix}/${index}`, result);
    });
  } else if (obj && typeof obj === "object") {
    for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
      flattenObject(v, `${prefix}/${k}`, result);
    }
  }
};

export const localeCopyApi = (config: ApiConfiguration) => ({
  async getCmsCopy(storiesPrefixes: string[], signal: AbortSignal): Promise<CmsCopy> {
    const response = await config.cmsService.getAll<CmsStoryContent>(storiesPrefixes, signal);

    const processLocaleCopy = (response: Record<string, CmsStoryContent>): Record<string, string> =>
      Object.entries(response).reduce((result: Record<string, string>, [key, obj]) => {
        const slug = getTranslatedSlug(key, config);
        const textProperties: { [key: string]: string | undefined } = {
          [slug]: obj.Text ?? obj.DisplayedName,
          [`${slug}/title`]: obj.Title,
          [`${slug}/sub-text`]:
            typeof obj.SubText === "string"
              ? obj.SubText
              : obj.SubText?.content?.[0]?.content?.[0]?.text,
          [`${slug}/box-title`]: obj.BoxTitle,
          [`${slug}/content-example`]: obj.ContentExample,
          [`${slug}/main-text`]: obj.MainText,
          [`${slug}/tooltip-text`]: obj.TooltipText,
          [`${slug}/maintenances`]: JSON.stringify(obj.Maintenances?.at(-1)),
        };

        Object.entries(textProperties).forEach(([key, value]) => {
          if (value) result[key] = value;
        });

        flattenObject(obj, slug, result);

        return result;
      }, {});

    const processOtherStories = (response: Record<string, CmsStoryContent>): Record<string, CmsStoryContent> => {
      return Object.entries(response).reduce((result: Record<string, CmsStoryContent>, [key, obj]) => {
        const slug = getTranslatedSlug(key, config);
        const identifier = key.split("/").pop() || "";
        result[slug] = { ...obj, slug: identifier };
        return result;
      }, {});
    };

    const cmsCopy = {
      localeCopy: processLocaleCopy(response),
      otherStories: processOtherStories(response),
    };

    return cmsCopy;
  },
});
